import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Shimmer } from "../Shimmer/Shimmer";
import { PolicyCheckTable } from "../PolicyCheckTable/PolicyCheckTable";
import { CreatePolicyCheckModal } from "../CreatePolicyCheckModal/CreatePolicyCheckModal";
import { TariffAccessBanner } from "../TariffAccessBanner/TariffAccessBanner";
import { usePolicyCheckCompanies } from "../../hooks/usePolicyCheckCompanies";
import { useTariffAccess } from "../../hooks/useTariffAccess";
import type { AdminTabId } from "../AdminWorkspace/adminTabs";
import "../RequestsSection/RequestsSection.scss";

interface PolicyCheckSectionProps {
  enabled?: boolean;
  showHeader?: boolean;
  embedded?: boolean;
  returnAdminTab?: AdminTabId;
}

export const PolicyCheckSection = ({
  enabled = true,
  showHeader = true,
  embedded = false,
  returnAdminTab,
}: PolicyCheckSectionProps) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { data, totalPages, loading, error, refetch } = usePolicyCheckCompanies({
    page,
    pageSize,
    enabled,
  });
  const tariffAccess = useTariffAccess();
  const createDisabled = !tariffAccess.canPolicyCheck;
  const policyBanner = tariffAccess.policyCheckBanner;
  const policyBannerBlockReason =
    tariffAccess.policyCheckBlockReason === "limit_reached"
      ? ("exhausted_policy" as const)
      : tariffAccess.policyCheckBlockReason === "disabled"
        ? ("type_disabled" as const)
        : tariffAccess.policyCheckBlockReason === "pending_payment"
          ? ("pending_payment" as const)
          : tariffAccess.policyCheckBlockReason === "tariff_requested"
            ? ("tariff_requested" as const)
            : tariffAccess.policyCheckBlockReason === "period_expired"
              ? ("period_expired" as const)
              : tariffAccess.policyCheckBlockReason === "no_tariff"
                ? ("no_tariff" as const)
                : null;

  if (!enabled) {
    return null;
  }

  return (
    <section
      className={`requests-section${embedded ? " requests-section--embedded" : ""}`}
      aria-label="Проверка политики обработки ПД"
    >
      {showHeader && (
        <div className="requests-section-header">
          <h1 className="requests-section-title">Проверка политики обработки ПД</h1>
          <button
            type="button"
            className="requests-section-create-btn"
            disabled={createDisabled}
            onClick={() => setShowCreateModal(true)}
          >
            <Plus size={20} />
            <span>Создать проверку</span>
          </button>
        </div>
      )}

      {policyBanner ? (
        <TariffAccessBanner
          banner={policyBanner}
          blockReason={policyBannerBlockReason}
        />
      ) : null}

      {loading ? (
        <Shimmer rows={5} />
      ) : error ? (
        <div className="requests-section-error">
          <p className="requests-section-error__text">{error}</p>
          <button type="button" className="requests-section-error__retry" onClick={refetch}>
            Повторить
          </button>
        </div>
      ) : (
        <PolicyCheckTable
          data={data}
          page={page}
          pageSize={pageSize}
          totalPages={totalPages}
          onRowClick={(item) =>
            item.id &&
            navigate(`/policy-check/${item.id}`, {
              state: {
                companyName: item.companyName,
                inn: item.inn,
                adminTab: returnAdminTab,
              },
            })
          }
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
        />
      )}

      {showCreateModal && (
        <CreatePolicyCheckModal
          onClose={() => setShowCreateModal(false)}
          onCreated={({ fileId, companyName, inn }) => {
            setShowCreateModal(false);
            refetch();
            navigate(`/policy-check/result/${fileId}`, {
              state: { companyName, inn, adminTab: returnAdminTab },
            });
          }}
        />
      )}
    </section>
  );
};
